"use client";

import React, { useCallback, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CreateUserBankAccountFormSchema } from "./schema";
import type { z } from "zod";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import type { Plan, User } from "@/actions/types";
import hooks from "@/hooks";
import helpers from "@/helpers";
import Modal from "@/components/core/Modal";

type BankAccount = z.infer<typeof CreateUserBankAccountFormSchema>;

interface ICreateUserBankAccountForm {
  user: User;
  plan: Plan;
  onFormChange?: (values: {
    cardNumber: string;
    name: string;
    expirationDate: string;
    cvv: string;
  }) => void;
}

const CreateUserBankAccountForm = ({
  user,
  plan,
  onFormChange,
}: ICreateUserBankAccountForm) => {
  const form = useForm<BankAccount>({
    resolver: zodResolver(CreateUserBankAccountFormSchema),
    defaultValues: {
      name: user.name,
      email: user.email,
      cardNumber: "",
      expirationDate: "",
      cvv: "",
      saveCard: false,
    },
  });

  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);

  const formatCardNumber = useCallback((value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  }, []);

  const formatExpirationDate = useCallback((value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");

    if (v.length > 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }

    return v;
  }, []);

  const formatCVV = useCallback((value: string) => {
    return value.replace(/\D/g, "").substring(0, 4);
  }, []);

  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "cardNumber" && value.cardNumber) {
        const formatted = formatCardNumber(value.cardNumber);
        if (formatted !== value.cardNumber) {
          form.setValue("cardNumber", formatted);
        }
      }

      if (name === "expirationDate" && value.expirationDate) {
        const formatted = formatExpirationDate(value.expirationDate);
        if (formatted !== value.expirationDate) {
          form.setValue("expirationDate", formatted);
        }
      }

      if (name === "cvv" && value.cvv) {
        const formatted = formatCVV(value.cvv);
        if (formatted !== value.cvv) {
          form.setValue("cvv", formatted);
        }
      }

      if (onFormChange) {
        onFormChange({
          cardNumber: value.cardNumber || "",
          name: value.name || "",
          expirationDate: value.expirationDate || "",
          cvv: value.cvv || "",
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, onFormChange, formatCardNumber, formatExpirationDate, formatCVV]);

  const formatCurrency = (value: string) => {
    if (value.includes("R$")) return value;

    const numValue = Number.parseFloat(
      value.replace(/[^\d.,]/g, "").replace(",", "."),
    );
    if (!isNaN(numValue)) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(numValue);
    }

    return value;
  };

  const userBankAccountService = hooks.useCreateUserBankAccount({
    onSuccess: () => {
      setSuccessModalOpen(true);
    },
    onError: (error) => {},
  });

  async function handleLogout() {
    await helpers.logoutHelper();
  }

  const AddressDetails = hooks.useGetAddressDetails(user.id).data;

  async function onSubmit(data: BankAccount) {
    if (!AddressDetails) {
      return;
    }

    const [expiryMonth, expiryYear] = data.expirationDate.split("/");

    const fullYear = `20${expiryYear}`;

    await userBankAccountService.mutateAsync({
      user_id: user.id,
      salon_id: user.salon_id || "",
      creditCard: {
        holderName: data.name,
        number: data.cardNumber.replace(/\s/g, ""),
        expiryMonth,
        expiryYear: fullYear,
        ccv: data.cvv,
      },
      creditCardHolderInfo: {
        name: data.name,
        email: data.email,

        cpfCnpj: localStorage.getItem("cpfCnpj") || "",

        postalCode: AddressDetails[0].zip_code || "",
        addressNumber: AddressDetails[0].number || "",
        addressComplement: AddressDetails[0].complement || null,

        phone: user.phone,
        mobilePhone: user.phone,
      },
    });
  }

  return (
    <>
      <div className="w-full bg-white p-4 sm:p-6 md:p-8 rounded-3xl shadow-lg flex flex-col">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="p-4 sm:p-6 md:p-8 w-full flex flex-col items-center gap-y-4 sm:gap-y-6"
          >
            <div className="w-full grid grid-cols-1 gap-4">
              <FormFields.InputFormField
                form={form}
                formName="name"
                label="Nome completo"
                noClearButton
                disabled={true}
              />
              <FormFields.InputFormField
                form={form}
                formName="email"
                label="E-mail"
                noClearButton
                disabled={true}
              />

              <FormFields.InputFormField
                form={form}
                formName="cardNumber"
                label="Número do cartão de crédito"
                placeholder="xxxx xxxx xxxx xxxx"
                mask={"creditCard"}
              />
            </div>

            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormFields.InputFormField
                form={form}
                formName="expirationDate"
                label="Data de expiração"
                placeholder="MM/YY"
              />

              <FormFields.InputFormField
                type="password"
                form={form}
                formName="cvv"
                label="CVV"
                placeholder="***"
              />
            </div>

            <div className="w-full border-t border-gray-300 my-2 sm:my-3"></div>

            <div className="h-5 justify-between items-center inline-flex w-full">
              <div className="text-slate-600 text-sm font-normal font-['Inter'] leading-tight tracking-tight">
                Total
              </div>
              <div className="text-right text-gray-950 text-sm font-semibold font-['Inter'] leading-tight tracking-tight">
                {formatCurrency(plan.value)}
              </div>
            </div>

            <div className="w-full">
              <div className="flex items-start sm:items-center space-x-2">
                <input
                  type="checkbox"
                  id="saveCard"
                  className="h-3 w-3 mt-1 sm:mt-0 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                  {...form.register("saveCard")}
                />
                <label
                  htmlFor="saveCard"
                  className="text-xs sm:text-sm text-gray-700"
                >
                  Concordo com o armazenamento das informações para fins de
                  pagamento recorrente.
                </label>
              </div>
            </div>

            <button
              id="hiddenSubmitButtonPayment"
              type="submit"
              className="hidden"
            />
          </form>
        </Form>
      </div>
      <Modal
        isOpen={isSuccessModalOpen}
        onOpenChange={setSuccessModalOpen}
        dialogTitle="Plano contratado com sucesso!"
        dialogDescription="Com a Wake Beauty você tem acesso ao calendário com os agendamentos dos seus clientes."
        className="flex flex-col items-center text-center p-6 sm:p-8 max-w-md"
        noDefaultCloseButton={true}
      >
        <div className="bg-alert-success-dark rounded-full p-3">
          <svg
            className="text-white w-10 h-10"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5"></path>
          </svg>
        </div>

        <div className="self-stretch text-center justify-start text-color-content-secondary text-xl font-bold tracking-tight">
          Plano contratado com sucesso!
        </div>

        <p className="text-gray-600 text-sm">
          Com a Wake Beauty você tem acesso ao calendário com os agendamentos
          dos seus clientes.
        </p>

        <button
          className="bg-alert-success-dark text-white w-full sm:w-auto mt-4 px-6 py-2 rounded-full"
          onClick={() => handleLogout()}
        >
          Acessar
        </button>
      </Modal>
    </>
  );
};

export default CreateUserBankAccountForm;
